import TagManager from 'react-gtm-module';
import get from 'lodash/get';
import moment from 'moment';

import config from '../../config';
import {
  getMakerAndModelAndCategoryForSearchFormByCanNames,
  getMakerAndModelAndCategoryForSearchFormByIds,
} from '../../ducks/AirplanesData.duck';

import { updatedEntities, denormalisedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import {
  plausibleGoals,
  trackEvent,
} from '../../util/plausible';
import { createImageVariantConfig } from '../../util/sdkLoader';
import {
  prepareSearchParams,
  setInitialFilterValues,
} from '../../util/search';
import { parse } from '../../util/urlHelpers';
import {
  fetchCategories as fetchAirplanesCategories, fetchAirplanesMetadata,
} from '../../modules/airplanes/airplanes.creator';

import { DATE_FORMAT } from '../../util/listing';
import { getIsBrokerAccount } from '../../util/user.path';
import { handleAxiosResponse } from '../../util/apiHelpers';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { restApiService } from '../../services/services';
import { getListingPhone } from '../../util/listing.path';
import {
  FILTER_KEY_AIRFRAME_TIME,
  FILTER_KEY_CATEGORY,
  FILTER_KEY_ENGINE_HOURS_1,
  FILTER_KEY_ENGINE_HOURS_2,
  FILTER_KEY_ENGINE_HOURS_3,
  FILTER_KEY_ENGINE_HOURS_4,
  FILTER_KEY_HAS_ACCIDENT_HISTORY,
  FILTER_KEY_HOME_TYPE,
  FILTER_KEY_HOURS_FLOWN,
  FILTER_KEY_MAKER,
  FILTER_KEY_MODEL,
  FILTER_KEY_MODEL_FAMILY,
  FILTER_KEY_PRICE,
  FILTER_KEY_PRICE_DECREASE,
  FILTER_KEY_PRICE_INCREASE,
  FILTER_KEY_SEATS,
  FILTER_KEY_YEAR,
  FILTER_KEYS_ENGINE_HOURS,
} from '../../components/FilterComponent/FilterComponent.constants';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 42 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 40;

const initialSearchValues = {
  [FILTER_KEY_MAKER]: null,
  [FILTER_KEY_MODEL]: null,
  [FILTER_KEY_MODEL_FAMILY]: null,
  [FILTER_KEY_CATEGORY]: null,
  [FILTER_KEY_PRICE]: null,
  [FILTER_KEY_YEAR]: null,
  [FILTER_KEY_AIRFRAME_TIME]: null,
  [FILTER_KEY_ENGINE_HOURS_1]: null,
  [FILTER_KEY_ENGINE_HOURS_2]: null,
  [FILTER_KEY_ENGINE_HOURS_3]: null,
  [FILTER_KEY_ENGINE_HOURS_4]: null,
  [FILTER_KEY_SEATS]: null,
  [FILTER_KEY_PRICE_INCREASE]: null,
  [FILTER_KEY_PRICE_DECREASE]: null,
  [FILTER_KEY_HOURS_FLOWN]: null,
  [FILTER_KEY_HOME_TYPE]: null,
  [FILTER_KEY_HAS_ACCIDENT_HISTORY]: null,
};

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/ManageListingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/ManageListingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/ManageListingsPage/FETCH_LISTINGS_ERROR';

export const OPEN_LISTING_REQUEST = 'app/ManageListingsPage/OPEN_LISTING_REQUEST';
export const OPEN_LISTING_SUCCESS = 'app/ManageListingsPage/OPEN_LISTING_SUCCESS';
export const OPEN_LISTING_ERROR = 'app/ManageListingsPage/OPEN_LISTING_ERROR';

export const CLOSE_LISTING_REQUEST = 'app/ManageListingsPage/CLOSE_LISTING_REQUEST';
export const CLOSE_LISTING_SUCCESS = 'app/ManageListingsPage/CLOSE_LISTING_SUCCESS';
export const CLOSE_LISTING_ERROR = 'app/ManageListingsPage/CLOSE_LISTING_ERROR';

export const ADD_OWN_ENTITIES = 'app/ManageListingsPage/ADD_OWN_ENTITIES';

export const SET_SEARCH_VALUES = 'app/ManageListingsPage/SET_SEARCH_VALUES';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  queryParams: null,
  queryInProgress: false,
  queryListingsError: null,
  currentPageResultIds: [],
  ownEntities: {},
  openingListing: null,
  openingListingError: null,
  closingListing: null,
  closingListingError: null,
  searchValues: initialSearchValues,
};

const resultIds = data => data.data.map(l => l.id);

const merge = (state, sdkResponse) => {
  const apiResponse = sdkResponse.data;
  return {
    ...state,
    ownEntities: updatedEntities({ ...state.ownEntities }, apiResponse),
  };
};

const updateListingAttributes = (state, listingEntity) => {
  const oldListing = state.ownEntities.listing[listingEntity.id.uuid];
  const updatedListing = { ...oldListing, attributes: listingEntity.attributes };
  const ownListingEntities = {
    ...state.ownEntities.listing,
    [listingEntity.id.uuid]: updatedListing,
  };
  return {
    ...state,
    ownEntities: { ...state.ownEntities, listing: ownListingEntities },
  };
};

const manageListingsPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        queryInProgress: true,
        queryListingsError: null,
        currentPageResultIds: [],
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        queryInProgress: false,
      };
    case FETCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, queryInProgress: false, queryListingsError: payload };

    case OPEN_LISTING_REQUEST:
      return {
        ...state,
        openingListing: payload.listingId,
        openingListingError: null,
      };
    case OPEN_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        openingListing: null,
      };
    case OPEN_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        openingListing: null,
        openingListingError: {
          listingId: state.openingListing,
          error: payload,
        },
      };
    }

    case CLOSE_LISTING_REQUEST:
      return {
        ...state,
        closingListing: payload.listingId,
        closingListingError: null,
      };
    case CLOSE_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        closingListing: null,
      };
    case CLOSE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        closingListing: null,
        closingListingError: {
          listingId: state.closingListing,
          error: payload,
        },
      };
    }

    case ADD_OWN_ENTITIES:
      return merge(state, payload);

    case SET_SEARCH_VALUES:
      return {
        ...state,
        searchValues: {
          ...state.searchValues,
          ...payload,
        },
      };

    default:
      return state;
  }
};

export default manageListingsPageReducer;

// ================ Selectors ================ //

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.ManageListingsPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'listing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

export const $searchValues = state => {
  return state.ManageListingsPage.searchValues;
};

// ================ Action creators ================ //

// This works the same way as addMarketplaceEntities,
// but we don't want to mix own listings with searched listings
// (own listings data contains different info - e.g. exact location etc.)
export const addOwnEntities = sdkResponse => ({
  type: ADD_OWN_ENTITIES,
  payload: sdkResponse,
});

export const openListingRequest = listingId => ({
  type: OPEN_LISTING_REQUEST,
  payload: { listingId },
});

export const openListingSuccess = response => ({
  type: OPEN_LISTING_SUCCESS,
  payload: response.data,
});

export const openListingError = e => ({
  type: OPEN_LISTING_ERROR,
  error: true,
  payload: e,
});

export const closeListingRequest = listingId => ({
  type: CLOSE_LISTING_REQUEST,
  payload: { listingId },
});

export const closeListingSuccess = response => ({
  type: CLOSE_LISTING_SUCCESS,
  payload: response.data,
});

export const closeListingError = e => ({
  type: CLOSE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = queryParams => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: { queryParams },
});

export const queryListingsSuccess = response => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const queryListingsError = e => ({
  type: FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});


const setSearchValuesAction = values => ({
  type: SET_SEARCH_VALUES,
  payload: values,
});

export const setSearchValues = values => dispatch => {
  dispatch(setSearchValuesAction(values));
};

// Throwing error for new (loadData may need that info)
export const queryOwnListings = queryParams => (dispatch) => {
  dispatch(queryListingsRequest(queryParams));

  const searchParams = prepareSearchParams({}, queryParams);
  const { perPage, ...rest } = queryParams;
  const params = { ...rest, per_page: perPage };
  return restApiService.secureInstance
    .getRaw(`brokers/get-dealer-listings`, { params })
    .then(async response => {
      dispatch(fetchAirplanesMetadata(response.data.data));
      dispatch(addOwnEntities(response));
      dispatch(fetchAirplanesCategories());
      dispatch(setInitialFilterValues(params, searchParams, setSearchValues));
      dispatch(queryListingsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(queryListingsError(storableError(e)));
      throw e;
    });
};

export const closeListing = (listing, data) => (dispatch, getState, sdk) => {
  const listingId = listing.id.uuid;
  dispatch(closeListingRequest(listingId));
  return restApiService.secureInstance
    .postRaw('brokers/close-dealer-listing', {
      listingId,
      closingReason: data,
    })
    .then(response => {
      dispatch(closeListingSuccess(response));
      trackEvent(
        plausibleGoals.closedListings,
        {props: { reasonClosedListing: data.reason }},
      );
      return response;
    })
    .then(() => {
      TagManager.dataLayer({
        dataLayer: {
          event: 'listingClosingReason',
          listing_id: listingId,
          reason: data.reason,
          salePrice: data.salePrice
        },
      });
      return Promise.resolve();
    })
    .catch(e => {
      dispatch(closeListingError(storableError(e)));
    });
};

const openListing = listing => (dispatch, getState, sdk) => {
  const listingId = listing.id.uuid;
  dispatch(openListingRequest(listingId));
  return sdk.ownListings
    .open({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(openListingSuccess(response));
      return response;
    })
  ;
}

export const openAircraftListing = listing => (dispatch, getState, sdk) => {
  const listingId = listing.id.uuid;
  dispatch(openListingRequest(listingId));
  return restApiService.secureInstance
    .postRaw('brokers/open-dealer-listing', {
      listingId,
    })
    .then((resp) => {
      dispatch(openListingSuccess(resp));
      return resp;
    })
    .catch(e => {
      dispatch(openListingError(storableError(e)));
    });
};

export const openRealEstateListing = listing => (dispatch, getState, sdk) => {
  const listingId = listing.id.uuid;
  return dispatch(openListing(listingId))
    .then(() => {
      const listing = get(
        getState(),
        `ManageListingsPage.ownEntities.listing[${listingId}]`,
        null
      );
      const priceHistory = get(listing, 'attributes.publicData.price_history', []);

      const realEstateType = get(listing, 'attributes.publicData.real_estate_listing_type', 0);
      const price = get(listing, 'attributes.price.amount', 0);

      return sdk.ownListings.update({
        id: listingId,
        publicData: {
          listingClosingReason: { },
          history_default_price: price,
          price_history: [
            ...priceHistory,
            {
              date: `${moment().tz("America/New_York").format(DATE_FORMAT)}`,
              type: 'manual',
              price,
              realEstateType,
              defaultPrice: null,
            },
          ],
        },
      });
    })
    .catch(e => {
      dispatch(openListingError(storableError(e)));
    });
};

export const loadData = (params, search) => dispatch => {
  const queryParams = parse(search);
  const page = queryParams.page || 1;

  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } = config.listing;
  const aspectRatio = aspectHeight / aspectWidth;

  dispatch(queryOwnListings({
    ...queryParams,
    page,
    perPage: RESULT_PAGE_SIZE,
    include: ['author', 'images', 'currentStock'],
    'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  }));
};
